/**
 * jrSound — tiny, zero-asset sound system for the elementary demo.
 * Uses the Web Audio API to synthesize kid-friendly tones (ding, chime,
 * celebration, error) without downloading any sound files.
 *
 * Persists a global mute preference to localStorage.
 */

const MUTE_KEY = "lemonade_jr_muted_v1";

let _ctx = null;
function ctx() {
  if (typeof window === "undefined") return null;
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  // Resume after user gesture (browser autoplay policy).
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

export function isMuted() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setMuted(m) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MUTE_KEY, m ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/** Schedule a single sine/triangle tone. */
function tone(freq, durationMs, { type = "sine", volume = 0.15, startOffsetMs = 0 } = {}) {
  if (isMuted()) return;
  const c = ctx();
  if (!c) return;
  const t0 = c.currentTime + startOffsetMs / 1000;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durationMs / 1000);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + durationMs / 1000 + 0.02);
}

/** Short "click" for drag/drop pickup. */
export function playPickup() {
  tone(520, 80, { type: "triangle", volume: 0.12 });
}

/** Coin "plink" when a coin lands on the register. */
export function playCoin() {
  tone(880, 70, { type: "triangle", volume: 0.13 });
  tone(1320, 90, { type: "sine", volume: 0.08, startOffsetMs: 40 });
}

/** Wrong / gentle "nope" buzz. */
export function playError() {
  tone(220, 120, { type: "sawtooth", volume: 0.08 });
  tone(180, 160, { type: "sawtooth", volume: 0.06, startOffsetMs: 80 });
}

/** Correct answer ding. */
export function playCorrect() {
  tone(660, 120, { type: "sine", volume: 0.14 });
  tone(990, 180, { type: "sine", volume: 0.12, startOffsetMs: 90 });
}

/** Full celebration (on completion). */
export function playCelebrate() {
  // C major arpeggio up, then a sparkle.
  [523, 659, 784, 1047].forEach((f, i) => {
    tone(f, 180, { type: "triangle", volume: 0.14, startOffsetMs: i * 110 });
  });
  tone(1568, 260, { type: "sine", volume: 0.1, startOffsetMs: 480 });
}

/** Soft UI click for buttons. */
export function playClick() {
  tone(420, 45, { type: "square", volume: 0.07 });
}
